// Visibility rules for the floating one-back navigation arrow.
//
// The control must only appear when tapping it would actually take the user to a
// real previous in-app route. In particular it must never render on the
// dashboard/home screen (there is nothing to go "one back" to), and it must be
// hidden — not rendered as a dead button — whenever no valid previous route
// exists (e.g. a fresh deep-link or refresh where navigate(-1) would leave the
// app). Kept as a pure module so the behavior is unit-testable without booting
// the whole App tree.

const normalizePath = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (raw === "/") return "/";
  // Ignore any trailing slash so "/dashboard" and "/dashboard/" match.
  return raw.replace(/\/+$/, "");
};

// Routes that are always the app "home" and therefore never show the arrow,
// regardless of the role-specific landing path.
const DASHBOARD_HOME_ROUTES = new Set(["/", "/dashboard"]);

export const isDashboardHomeRoute = (pathname = "", landingPath = "/dashboard") => {
  const normalized = normalizePath(pathname);
  if (!normalized) return false;
  if (normalized === normalizePath(landingPath)) return true;
  return DASHBOARD_HOME_ROUTES.has(normalized);
};

// react-router marks the very first history entry with the key "default"; any
// real in-app navigation produces a unique key. A non-default key together with
// real browser history depth means navigate(-1) has somewhere to go.
export const hasValidPreviousRoute = ({ locationKey = "", historyLength = 0 } = {}) =>
  Boolean(locationKey) && locationKey !== "default" && Number(historyLength) > 1;

export const shouldShowOneBackButton = ({
  pathname = "",
  landingPath = "/dashboard",
  isExcludedPage = false,
  locationKey = "",
  historyLength = 0,
} = {}) => {
  if (isExcludedPage) return false;
  if (isDashboardHomeRoute(pathname, landingPath)) return false;
  return hasValidPreviousRoute({ locationKey, historyLength });
};
